import PropTypes from "prop-types";
import { useCallback, useContext, useEffect, useState, useMemo } from "react";
import LandingPageListing from "app/pages/LandingPage/LandingPageListing/LandingPageListing";
import { forceCheck } from "react-lazyload";
import { clearProduct as clearProductAction } from "app/pages/Listing/productActionCreators";
import {
	getProductsOfLandingPage,
	shouldLandingPageHideProductPriceBanner,
	getLandingPageMeta,
	getCurrentLandingPage,
} from "app/pages/LandingPage/landingPageSelector";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchLandingPage as fetchLandingPageAction } from "app/pages/LandingPage/landingPageActionCreators";
import {
	getPartnerListingSEO,
	getPartnerMarketingHomeBlocks,
	shouldShowPaymentBanner,
} from "app/reducers/partnerSelector";
import { resetAllProductsFilter as resetAllProductsFilterAction } from "app/pages/Listing/ProductFilters/productsFilterActionCreators";
import AppGlobalsContext from "app/AppGlobalsContext";
import { resetAllSDPProductsFilter as resetAllSDPProductsFilterAction } from "app/pages/SmartDP/Listing/SmartDPFilters/smartDPProductsFilterActionCreators";
import { updateSDPFormDestination as updateSDPFormDestinationAction } from "app/pages/Booking/bookingActionCreators";
import {
	SDP_SEARCH_MARKETING_BLOCKS,
	PRODUCTS_FILTERS_KEY,
	PRODUCT_TAG_CODES,
	MULTI_STAGE_JOURNEY_OPTIONS,
	OFFER_TYPE_LABELS,
} from "app/constants";
import {
	FilterProvider,
	OPTION_MODE,
	isMultiStageJourneyProduct,
	getDestinationGetter,
} from "app/features/product/productFilters";
import "./LandingPage.scss";
import { useIntl } from "react-intl";
import { SortProvider } from "app/features/product/productSort";
import { fetchSDPFilters as fetchSDPFiltersAction } from "app/pages/SmartDP/smartDPActionCreators";

const CUSTOM_COMPARATORS = {
	recommendation: (a, b) => (a?.rank || 0) - (b?.rank || 0),
	price_asc: (a, b) => (a?.fromPriceType?.value ?? 0) - (b?.fromPriceType?.value ?? 0),
	price_desc: (a, b) => (b?.fromPriceType?.value ?? 0) - (a?.fromPriceType?.value ?? 0),
};

const LandingPageContainer = props => {
	const { clearBooking = () => {} } = props;

	const { landingPageCode } = useParams();

	const stateProps = useSelector(mapStateToProps, shallowEqual);
	const { currentLandingPage, destinations = [], productsOfLandingPage } = stateProps;

	const dispatch = useDispatch();
	const clearProduct = useCallback(
		(...args) => dispatch(clearProductAction(...args)),
		[dispatch]
	);
	const resetAllProductsFilter = useCallback(
		(...args) => dispatch(resetAllProductsFilterAction(...args)),
		[dispatch]
	);
	const resetAllSDPProductsFilter = useCallback(
		(...args) => dispatch(resetAllSDPProductsFilterAction(...args)),
		[dispatch]
	);
	const fetchSDPFilters = useCallback(
		(...args) => dispatch(fetchSDPFiltersAction(...args)),
		[dispatch]
	);
	const updateSDPFormDestination = useCallback(
		(...args) => dispatch(updateSDPFormDestinationAction(...args)),
		[dispatch]
	);
	const fetchLandingPage = useCallback(
		(...args) => dispatch(fetchLandingPageAction(...args)),
		[dispatch]
	);

	const dispatchProps = {
		clearProduct,
		resetAllProductsFilter,
		resetAllSDPProductsFilter,
		fetchSDPFilters,
		updateSDPFormDestination,
		fetchLandingPage,
	};

	const [loading, setLoading] = useState(false);
	const { formatMessage, formatDate } = useIntl();

	const { shop, productsVisibility, partnerCode } = useContext(AppGlobalsContext);

	useEffect(() => {
		resetAllProductsFilter();
		fetchSDPFilters();
		clearBooking();
		if (landingPageCode) {
			fetchLandingPage({
				landingPageCode,
				shop,
				productsVisibility,
				partnerCode,
			}).then(() => {
				setLoading(false);
				forceCheck();
			});
		}
	}, [landingPageCode]);

	const landingPageDestinationResort = currentLandingPage?.destinationFilterValues
		? destinations.find(
				destination => destination.label === currentLandingPage?.destinationFilterValues[0]
			)
		: undefined;

	useEffect(() => {
		const hideSmartDPForm = currentLandingPage?.layout?.hideSmartDPForm;

		if (
			!hideSmartDPForm &&
			currentLandingPage?.destinationFilterValues?.length === 1 &&
			landingPageDestinationResort
		) {
			updateSDPFormDestination({
				code: landingPageDestinationResort.code.toString(),
				label: landingPageDestinationResort.label,
			});
		}
	}, [currentLandingPage?.code, landingPageDestinationResort?.code]);

	const shouldShowMultiStageJourneyOption = useMemo(
		() => productsOfLandingPage?.some(isMultiStageJourneyProduct),
		[productsOfLandingPage]
	);

	const destinationGetter = useMemo(
		() => getDestinationGetter(productsOfLandingPage),
		[productsOfLandingPage]
	);

	const facetConfigs = useMemo(
		() => [
			{
				facetId: PRODUCTS_FILTERS_KEY.DESTINATION,
				facetTitle: formatMessage({ id: "product.page.filter.destinations.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.DESTINATION,
						title: formatMessage({ id: "product.page.filter.destinations.label" }),
						valueGetter: destinationGetter,

						...(shouldShowMultiStageJourneyOption && {
							buildOptions: () => [
								{
									value: "multi-stage-journey",
									label: formatMessage({
										id: "listing.filter.destination.multi.destinations.label",
									}),
								},
							],
							customMatcher: isMultiStageJourneyProduct,
						}),

						allowSearch: true,
						search: { placeholderKey: "filter.destination.searchbar.placeholder" },
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.OFFER_TYPE,
				facetTitle: formatMessage({ id: "product.page.filter.offer.type.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.OFFER_TYPE,
						title: formatMessage({ id: "product.page.filter.offer.type.label" }),
						valueGetter: product => product.merchandisings,
						buildOptions: products => {
							const offerTypes = products.reduce(
								(acc, { merchandisings = [] }) => {
									const hasGC = merchandisings?.some(m =>
										m?.startsWith(PRODUCT_TAG_CODES.GC)
									);

									const hasCDC = merchandisings?.some(m =>
										m?.startsWith(PRODUCT_TAG_CODES.COUP_DE_COEUR)
									);

									return {
										hasGC: acc.hasGC || hasGC,
										hasOnlyCDCProduct:
											acc.hasOnlyCDCProduct || (!hasGC && hasCDC),
									};
								},
								{ hasGC: false, hasOnlyCDCProduct: false }
							);

							return [
								...(offerTypes.hasGC ? [PRODUCT_TAG_CODES.GC] : []),
								...(offerTypes.hasOnlyCDCProduct
									? [PRODUCT_TAG_CODES.COUP_DE_COEUR]
									: []),
							].map(merchandising => ({
								value: merchandising,
								labelKey: OFFER_TYPE_LABELS[merchandising] ?? merchandising,
							}));
						},
						customMatcher: (product, selectedValues) => {
							const productOfferTypes = product.merchandisings;

							return selectedValues?.some(selectedValue => {
								if (selectedValue === PRODUCT_TAG_CODES.COUP_DE_COEUR) {
									const hasCDC = productOfferTypes?.some(offerType =>
										offerType.includes(PRODUCT_TAG_CODES.COUP_DE_COEUR)
									);
									const hasGC = productOfferTypes?.some(offerType =>
										offerType.includes(PRODUCT_TAG_CODES.GC)
									);
									return hasCDC && !hasGC;
								}
								return productOfferTypes?.some(offerType =>
									offerType.startsWith(selectedValue)
								);
							});
						},
						allowSingleOption: true,
						optionMode: OPTION_MODE.MANUAL_ONLY,
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.MULTI_STAGE_JOURNEY,
				facetTitle: formatMessage({ id: "multi.stage.journey" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.MULTI_STAGE_JOURNEY,
						title: formatMessage({ id: "multi.stage.journey" }),
						valueGetter: product => product.productExperience,
						buildOptions: products =>
							[...new Set(products.map(product => product.productExperience))]
								.filter(option =>
									Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(option)
								)
								.map(option => ({
									value: option,
									label: formatMessage({
										id: MULTI_STAGE_JOURNEY_OPTIONS[option],
									}),
								})),
						optionMode: OPTION_MODE.MANUAL_ONLY,
						allowSingleOption: true,
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.TOPIC,
				facetTitle: formatMessage({ id: "listing.filter.theme.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.TOPIC,
						title: formatMessage({ id: "listing.filter.theme.label" }),
						valueGetter: item => item.topics,
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.MONTH,
				facetTitle: formatMessage({ id: "listing.filter.month.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.MONTH,
						title: formatMessage({ id: "listing.filter.month.label" }),
						valueGetter: item => item.months.map(month => month.label),
						formatLabel: ({ label }) => {
							const [year, month] = label.split("/");
							const date = new Date(Number(year), Number(month) - 1, 1);
							return formatDate(date, { month: "long", year: "numeric" });
						},
						customSort: (a, b) => a?.value?.localeCompare(b?.value),
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.PROPERTY_NAME,
				facetTitle: formatMessage({ id: "product.page.filter.property.name.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.PROPERTY_NAME,
						title: formatMessage({ id: "product.page.filter.property.name.label" }),
						allowSearch: true,
						search: { placeholderKey: "filter.property.name.searchbar.placeholder" },
						valueGetter: product => product.id,
						buildOptions: products =>
							products
								.filter(
									product =>
										!Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(
											product?.productExperience
										)
								)
								.map(product => ({ value: product.id, label: product.name }))
								.sort((a, b) => a.label.localeCompare(b.label)),
						optionMode: OPTION_MODE.MANUAL_ONLY,
						showCount: false,
					},
				],
			},
		],
		[shouldShowMultiStageJourneyOption, destinationGetter, formatMessage, formatDate]
	);

	return (
		<FilterProvider facetConfigs={facetConfigs} products={productsOfLandingPage}>
			<SortProvider comparators={CUSTOM_COMPARATORS}>
				<div className="landing-page-seo">
					<LandingPageListing
						{...props}
						{...stateProps}
						{...dispatchProps}
						loading={loading}
					/>
				</div>
			</SortProvider>
		</FilterProvider>
	);
};

LandingPageContainer.propTypes = {
	clearProduct: PropTypes.func,
	resetAllProductsFilter: PropTypes.func,
	partnerCode: PropTypes.string,
	resolution: PropTypes.string,
	fetchLandingPage: PropTypes.func,
	clearBooking: PropTypes.func,
	listingSEO: PropTypes.object,
	fetchSDPFilters: PropTypes.func,
	updateSDPFormDestination: PropTypes.func,
	destinations: PropTypes.array,
	currentLandingPage: PropTypes.object,
	productsOfLandingPage: PropTypes.array,
};

const mapStateToProps = state => {
	const homeBlocks = getPartnerMarketingHomeBlocks(state);

	const hasMembershipBlock =
		homeBlocks?.length > 0 &&
		homeBlocks?.findIndex(block => block._type === SDP_SEARCH_MARKETING_BLOCKS.MEMBERSHIP) !==
			-1;

	const currentLandingPage = state.landingPage.current;

	const hideMembershipBlock = currentLandingPage?.displayOptions?.hideMembershipBlock;

	return {
		resolution: state.resolution,
		currentLandingPage: getCurrentLandingPage(state),
		shouldHideProductPriceBanner: shouldLandingPageHideProductPriceBanner(state),
		listingSEO: getPartnerListingSEO(state),
		showMembershipBlock: hasMembershipBlock && !hideMembershipBlock,
		isPaymentDescriptionBlockExist: shouldShowPaymentBanner(state),
		landingPageMeta: getLandingPageMeta(state),
		partnerCode: state.partner.code,
		productsOfLandingPage: getProductsOfLandingPage(state),
		destinations: state.smartDP.destinations,
	};
};

export default LandingPageContainer;
